// Thin fetch wrapper around the TinyPages API. The frontend assumes
// same-origin (Vite proxies /api in dev; in prod the server hosts the
// bundle on the same host).

import i18n from "./i18n";

export interface Me {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  /** Display name for the drawing artist (often the user's child). */
  authorName: string | null;
  /** 4-digit year used to compute the artist's age for prompts + UI. */
  authorBirthYear: number | null;
  /** Preferred UI language ("en" | "zh-CN"); null until the user picks one. */
  uiLanguage: string | null;
  credits?: { balance: number | null; unlimited: boolean };
  /** Per-page credit cost (server constant), for "N credits per page" copy. */
  creditsPerPage?: number;
  /** True for ADMIN_EMAILS accounts — shows the beta-allowlist admin page. */
  isAdmin?: boolean;
}

/** One beta_allowlist row, as returned by GET /api/admin/allowlist. */
export interface AllowlistEntry {
  email: string;
  note: string | null;
  createdAt: string;
}

/**
 * Author info denormalised from the user profile by the server. `age` is
 * computed at request time from `authorBirthYear`, so it stays current
 * without us migrating stored numbers.
 */
export interface BookAuthor {
  name: string | null;
  age: number | null;
}

export interface Book {
  id: string;
  title: string;
  author: BookAuthor;
  status: "draft" | "queued" | "polishing" | "ready" | "failed";
  shortId: string;
  /** Owner responses only; absent on the public share payload. */
  viewCount?: number;
  uniqueReaderCount?: number;
  /** "photos" = user-supplied images (kid drawings, trip photos, daily snaps); "story" = parent-prompt generated. */
  mode: "photos" | "story";
  /** Story-mode only: the parent's brief. NULL for photos-mode books. */
  brief: string | null;
  /** Photos-mode only: optional context shared across every uploaded image. */
  storyBackground: string | null;
  /** Story-mode only: free-text language label. NULL for photos-mode. */
  language: string | null;
  /** Story-mode only: how many pages were requested. NULL for photos-mode. */
  pageCount: number | null;
  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  completedAt: string | null;
  archivedAt: string | null;
  supersededAt?: string | null;
  replacedByBookId?: string | null;
  revisionOf?: string | null;
  activeReplaceRevision?: { id: string; status: "queued" | "polishing" | "failed"; progress: number; totalPages: number } | null;
}

export type BookListView = "active" | "archived";

export type PageGenerationStatus = "queued" | "running" | "completed" | "failed";
export type PageGenerationSourceKind = "auto" | "photo" | "drawing";

export interface PageGeneration {
  id: string;
  bookId: string;
  pageId: string;
  polishJobId: string | null;
  op: "initial_polish" | "regenerate";
  status: PageGenerationStatus;
  sourceKind: PageGenerationSourceKind;
  userInstruction: string | null;
  outputKey: string | null;
  provider: string;
  model: string | null;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export function pageGenerationInFlight(g: PageGeneration | null | undefined): boolean {
  return g?.status === "queued" || g?.status === "running";
}

export interface Page {
  id: string;
  bookId: string;
  position: number;
  rotation: number;
  crop: { x: number; y: number; w: number; h: number } | null;
  description: string | null;
  /** Reader footer narrative. Story-mode page copy, or optional photos-mode generated narrative. */
  storyText: string | null;
  contentType: string | null;
  /** NULL for story-mode pages (no source upload). */
  originalUrl: string | null;
  polishedUrl: string | null;
  /** Owner API only; public share responses omit generation history. */
  latestGeneration?: PageGeneration | null;
}

export interface StagedUpload {
  key: string;
  contentType: string;
}

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export interface Job {
  id: string;
  bookId: string;
  status: "queued" | "running" | "completed" | "failed";
  provider: string;
  model: string | null;
  progress: number;
  totalPages: number;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

function mimeFromName(name: string): string | null {
  const ext = name.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1];
  switch (ext) {
    case "png": return "image/png";
    case "jpg":
    case "jpeg": return "image/jpeg";
    case "webp": return "image/webp";
    case "heic": return "image/heic";
    case "heif": return "image/heif";
    default: return null;
  }
}

export class InsufficientCreditsError extends Error {
  constructor(public readonly required: number, public readonly balance: number) {
    super(i18n.t("errors.insufficient_credits"));
    this.name = "InsufficientCreditsError";
  }
}

/** Server error codes that have a dedicated translation in errors.*. */
const TRANSLATED_ERROR_CODES = new Set([
  "rate_limited",
  "file_too_large",
  "unsupported_mime",
  "page_limit_reached",
  "upload_not_found",
  "book_in_flight",
  "too_few_pages",
  "not_found",
  "book_not_failed",
  "book_has_no_pages",
]);

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string) {
    super(
      TRANSLATED_ERROR_CODES.has(code)
        ? i18n.t(`errors.${code}`)
        : i18n.t("errors.generic", { code: `${status} ${code}` }),
    );
    this.name = "ApiError";
  }
}

/** fetch() that converts transport failures into a localized error. */
async function request(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch {
    throw new Error(i18n.t("errors.network"));
  }
}

export function isInsufficientCredits(e: unknown): e is InsufficientCreditsError {
  return e instanceof InsufficientCreditsError;
}

/** Maps a non-OK response to a localized InsufficientCreditsError/ApiError. */
async function throwForResponse(res: Response): Promise<never> {
  let detail = "";
  let body: unknown;
  try {
    body = await res.json();
    detail = (body as { error?: string })?.error ?? JSON.stringify(body);
  } catch {
    detail = await res.text().catch(() => "");
  }
  if (res.status === 402 && (body as { error?: string })?.error === "insufficient_credits") {
    const b = body as { required?: number; balance?: number };
    throw new InsufficientCreditsError(b.required ?? 0, b.balance ?? 0);
  }
  throw new ApiError(res.status, detail || "unknown");
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) await throwForResponse(res);
  return res.json() as Promise<T>;
}

export const api = {
  async me(): Promise<Me | null> {
    const res = await request("/api/me", { credentials: "include" });
    const data = (await jsonOrThrow<{ user: Me | null }>(res));
    return data.user;
  },

  async updateMe(patch: { authorName?: string | null; authorBirthYear?: number | null; uiLanguage?: string | null }): Promise<Me> {
    const res = await request("/api/me", {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    const { user } = await jsonOrThrow<{ user: Me }>(res);
    return user;
  },

  async logout(): Promise<void> {
    await request("/auth/logout", { method: "POST", credentials: "include" });
  },

  async version(): Promise<{ gitSha: string | null; buildTime: string | null; version: string }> {
    const res = await request("/api/version", { credentials: "include" });
    return jsonOrThrow(res);
  },

  async listBooks(view: BookListView = "active"): Promise<Book[]> {
    const res = await request(`/api/books?view=${view}`, { credentials: "include" });
    const { books } = await jsonOrThrow<{ books: Book[] }>(res);
    return books;
  },

  async archiveBook(id: string): Promise<Book> {
    const res = await request(`/api/books/${id}/archive`, {
      method: "POST",
      credentials: "include",
    });
    const { book } = await jsonOrThrow<{ book: Book }>(res);
    return book;
  },

  async restoreBook(id: string): Promise<Book> {
    const res = await request(`/api/books/${id}/restore`, {
      method: "POST",
      credentials: "include",
    });
    const { book } = await jsonOrThrow<{ book: Book }>(res);
    return book;
  },

  async deleteBook(id: string): Promise<void> {
    const res = await request(`/api/books/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    // 204 No Content on success — don't try to parse a body.
    if (!res.ok) await throwForResponse(res);
  },

  async createBook(input: Partial<Pick<Book, "title">>): Promise<Book> {
    const res = await request("/api/books", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const { book } = await jsonOrThrow<{ book: Book }>(res);
    return book;
  },

  async createStoryBook(input: {
    title: string;
    brief: string;
    language: string;
    pageCount: number;
    references?: Array<{ key: string; contentType: string; description?: string | null }>;
    revisionOf?: string;
    revisionIntent?: "replace";
  }): Promise<{ book: Book; job: Job }> {
    const res = await request("/api/books", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...input, mode: "story" }),
    });
    return jsonOrThrow(res);
  },

  /**
   * Story-mode only: stage a reference image upload. Returns the storage
   * key + content type, which the caller passes through in
   * `createStoryBook.references[]`. The server moves the staged object
   * into the per-book `refs/` prefix on book creation.
   */
  async stageReferenceUpload(file: File): Promise<StagedUpload> {
    if (file.size > MAX_UPLOAD_BYTES) {
      throw new Error(`reference image is too large; max ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)}MB`);
    }
    const contentType = file.type || mimeFromName(file.name) || "application/octet-stream";

    const presignRes = await request(`/api/books/refs/upload-url`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ contentType }),
    });
    const presigned = await jsonOrThrow<{
      key: string;
      contentType: string;
      uploadUrl: string;
      method: "PUT";
      headers: Record<string, string>;
    }>(presignRes);

    const putRes = await request(presigned.uploadUrl, {
      method: presigned.method,
      headers: presigned.headers,
      body: file,
      credentials: presigned.uploadUrl.startsWith("/") ? "include" : "omit",
    });
    if (!putRes.ok) {
      const body = await putRes.text().catch(() => "");
      throw new Error(`upload failed: ${putRes.status} ${body.slice(0, 200)}`);
    }
    return { key: presigned.key, contentType: presigned.contentType };
  },

  async getBook(id: string): Promise<{ book: Book; pages: Page[]; job: Job | null }> {
    const res = await request(`/api/books/${id}`, { credentials: "include" });
    return jsonOrThrow(res);
  },

  async updateBook(
    id: string,
    patch: Partial<Pick<Book, "title" | "storyBackground">>,
  ): Promise<Book> {
    const res = await request(`/api/books/${id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    const { book } = await jsonOrThrow<{ book: Book }>(res);
    return book;
  },

  // Two-step upload: stage (presign + PUT) can run in parallel for a batch,
  // commit must run sequentially in the user's pick order so the server-
  // assigned `position` matches the order the user tapped the photos in.
  async stagePageUpload(
    bookId: string,
    file: File,
    signal?: AbortSignal,
  ): Promise<StagedUpload> {
    // iOS Safari sometimes leaves File.type empty for HEIC/HEIF — fall back
    // to the extension so the server's MIME allowlist still accepts it.
    const contentType = file.type || mimeFromName(file.name) || "application/octet-stream";

    const presignRes = await request(`/api/books/${bookId}/pages/upload-url`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ contentType }),
      signal,
    });
    const presigned = await jsonOrThrow<{
      key: string;
      contentType: string;
      uploadUrl: string;
      method: "PUT";
      headers: Record<string, string>;
    }>(presignRes);

    const putRes = await request(presigned.uploadUrl, {
      method: presigned.method,
      headers: presigned.headers,
      body: file,
      // Same-origin in dev (Vite proxy / local mount), cross-origin to S3 in
      // prod — `omit` keeps the session cookie out of the S3 request and
      // matches what the presigner expects.
      credentials: presigned.uploadUrl.startsWith("/") ? "include" : "omit",
      signal,
    });
    if (!putRes.ok) {
      const body = await putRes.text().catch(() => "");
      throw new Error(`upload failed: ${putRes.status} ${body.slice(0, 200)}`);
    }

    return { key: presigned.key, contentType: presigned.contentType };
  },

  async commitPage(bookId: string, staged: StagedUpload): Promise<Page> {
    const res = await request(`/api/books/${bookId}/pages`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key: staged.key, contentType: staged.contentType }),
    });
    const { page } = await jsonOrThrow<{ page: Page }>(res);
    return page;
  },

  async patchPage(
    bookId: string,
    pageId: string,
    patch: {
      rotation?: number;
      crop?: Page["crop"];
      position?: number;
      description?: string | null;
    },
  ): Promise<Page[]> {
    const res = await request(`/api/books/${bookId}/pages/${pageId}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    const { pages } = await jsonOrThrow<{ pages: Page[] }>(res);
    return pages;
  },

  async deletePage(bookId: string, pageId: string): Promise<void> {
    const res = await request(`/api/books/${bookId}/pages/${pageId}`, {
      method: "DELETE",
      credentials: "include",
    });
    await jsonOrThrow<{ ok: true }>(res);
  },

  async getDemo(): Promise<{ book: Book; pages: Page[] } | null> {
    const res = await request("/api/demo");
    if (!res.ok) return null;
    return (await res.json()) as { book: Book; pages: Page[] };
  },

  async getSharedBook(
    shortId: string,
  ): Promise<
    | { ok: true; book: Book; pages: Page[] }
    | { ok: false; reason: "not_found" | "not_ready" }
  > {
    const res = await request(`/api/share/${encodeURIComponent(shortId)}`);
    if (res.ok) {
      const data = (await res.json()) as { book: Book; pages: Page[] };
      return { ok: true, book: data.book, pages: data.pages };
    }
    let reason: "not_found" | "not_ready" = "not_found";
    try {
      const body = (await res.json()) as { reason?: string };
      if (body.reason === "not_ready") reason = "not_ready";
    } catch {
      // body wasn't json — fall through to the default reason
    }
    return { ok: false, reason };
  },

  async recordView(
    shortId: string,
    body: { visitorId: string; dwellMs: number; interactions: number },
  ): Promise<void> {
    try {
      await request(`/api/share/${encodeURIComponent(shortId)}/view`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        keepalive: true,
      });
    } catch {
      // Best-effort analytics; never surface to the reader.
    }
  },

  async submitBook(bookId: string): Promise<{ book: Book; job: Job }> {
    const res = await request(`/api/books/${bookId}/submit`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    return jsonOrThrow(res);
  },

  async startEditSession(bookId: string): Promise<
    | { mode: "photos"; draftId: string }
    | { mode: "story"; revisionOf: string; prefill: { title: string; brief: string; language: string; pageCount: number; references: Array<{ key: string; contentType: string; description: string | null; previewUrl: string }> } }
  > {
    const res = await request(`/api/books/${bookId}/edit-session`, { method: "POST", credentials: "include" });
    return jsonOrThrow(res);
  },

  async submitPhotos(bookId: string, revisionIntent?: "new" | "replace"): Promise<{ book: Book; job: Job }> {
    const res = await request(`/api/books/${bookId}/submit`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(revisionIntent ? { revisionIntent } : {}),
    });
    return jsonOrThrow(res);
  },

  // ── Admin (ADMIN_EMAILS accounts only; server enforces) ────────────────
  async listAllowlist(): Promise<AllowlistEntry[]> {
    const res = await request("/api/admin/allowlist", { credentials: "include" });
    const { allowlist } = await jsonOrThrow<{ allowlist: AllowlistEntry[] }>(res);
    return allowlist;
  },

  async addAllowlistEmail(email: string, note?: string): Promise<void> {
    const res = await request("/api/admin/allowlist", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, ...(note ? { note } : {}) }),
    });
    await jsonOrThrow<{ ok: true }>(res);
  },

  async removeAllowlistEmail(email: string): Promise<void> {
    const res = await request(`/api/admin/allowlist/${encodeURIComponent(email)}`, {
      method: "DELETE",
      credentials: "include",
    });
    await jsonOrThrow<{ ok: true }>(res);
  },

  async retryBook(bookId: string): Promise<{ book: Book; job: Job }> {
    const res = await request(`/api/books/${bookId}/retry`, {
      method: "POST",
      credentials: "include",
    });
    return jsonOrThrow(res);
  },

  async regeneratePage(
    bookId: string,
    pageId: string,
    input: {
      sourceKind?: PageGenerationSourceKind;
      instruction?: string | null;
    },
  ): Promise<PageGeneration> {
    const res = await request(`/api/books/${bookId}/pages/${pageId}/regenerate`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const { generation } = await jsonOrThrow<{ generation: PageGeneration }>(res);
    return generation;
  },
};
